import { ChangeDetectionStrategy, Component, OnInit, computed, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';

import { AgentRequestApiService } from '../services/agent-request-api.service';
import { ToasterService } from '../../../../core/services/toaster.service';

/**
 * Header switch: "am I available to be pulled into a live demo?"
 *
 * ONLINE puts the signed-in user into the rotation that "Ask to Agent" rings; OFFLINE takes them
 * out. It is the user's own opt-in — nobody else can flip it, because the backend always writes
 * presence for the JWT's user and ignores any pid in the request.
 *
 * Going ONLINE also starts the inbox poll (see {@link AgentRequestApiService}), which is what
 * makes the ringing modal appear anywhere in the CRM. This component does not know that; it only
 * writes the status.
 *
 * Optimistic: the switch moves immediately and rolls back if the write fails, so it never sits
 * in a state the server does not actually hold.
 */
@Component({
  selector: 'app-presence-toggle',
  standalone: true,
  imports: [CommonModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <button type="button" role="switch" [attr.aria-checked]="isOnline()" [disabled]="busy()"
      (click)="toggle()" [title]="hint()"
      class="inline-flex items-center gap-2 rounded-full border border-[#EAEAEA] dark:border-line
             bg-white dark:bg-surface-1 px-2.5 py-1 transition-colors
             hover:bg-gray-50 dark:hover:bg-line disabled:opacity-60 disabled:cursor-wait">

      <!-- the track -->
      <span class="relative inline-block h-4 w-8 shrink-0 rounded-full transition-colors"
        [class]="isOnline() ? 'bg-emerald-500' : 'bg-gray-300 dark:bg-white/20'">
        <!-- the knob -->
        <span class="absolute top-0.5 h-3 w-3 rounded-full bg-white shadow transition-all"
          [class]="isOnline() ? 'left-4' : 'left-0.5'"></span>
      </span>

      <span class="text-xs font-medium"
        [class]="isOnline() ? 'text-emerald-600 dark:text-emerald-400' : 'text-gray-500 dark:text-ink-soft/70'">
        {{ busy() ? 'Saving…' : (isOnline() ? 'Online' : 'Offline') }}
      </span>
    </button>
  `,
})
export class PresenceToggleComponent implements OnInit {
  private readonly agentRequests = inject(AgentRequestApiService);
  private readonly toaster = inject(ToasterService);

  readonly busy = signal(false);
  readonly isOnline = computed(() => this.agentRequests.status() === 'ONLINE');

  readonly hint = computed(() =>
    this.isOnline()
      ? 'You are available — a customer on a live demo can ask to speak with you. Click to go offline.'
      : 'Customers cannot reach you from a live demo. Click to go online.',
  );

  ngOnInit(): void {
    void this.agentRequests.boot();
  }

  async toggle(): Promise<void> {
    if (this.busy()) return;
    const next = this.isOnline() ? 'OFFLINE' : 'ONLINE';
    this.busy.set(true);
    try {
      await this.agentRequests.setPresence(next);
      this.toaster.success(
        next === 'ONLINE'
          ? 'You are online — customers on a live demo can now reach you.'
          : 'You are offline.',
        'Availability',
      );
    } catch {
      this.toaster.error('Could not update your availability.', 'Availability');
    } finally {
      this.busy.set(false);
    }
  }
}
